import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { NetworkIcon, NetworkWiredIcon, RouteIcon, SecurityIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import GatewaysTab from './GatewaysTab';
import FirewallsTab from './FirewallsTab';
import SwitchesTab from './SwitchesTab';
import NetworksTab from './NetworksTab';

type NetworkingTabKey = 'gateways' | 'firewalls' | 'switches' | 'networks';

interface TabHeader {
  title: string;
  subtitle: string;
  icon: React.ComponentType;
}

const TAB_HEADERS: Record<NetworkingTabKey, TabHeader> = {
  gateways: {
    title: 'Gateways',
    subtitle: 'Manage the gateways for your organization.',
    icon: RouteIcon,
  },
  firewalls: {
    title: 'Firewalls',
    subtitle: 'Manage the firewalls and firewall clusters for your organization.',
    icon: SecurityIcon,
  },
  switches: {
    title: 'Switches',
    subtitle: 'Manage the switches for your organization.',
    icon: NetworkWiredIcon,
  },
  networks: {
    title: 'Networks',
    subtitle: 'Browse the IP networks (prefixes) assigned to your organization.',
    icon: NetworkIcon,
  },
};

function Networks() {
  const auth = useAuth();
  const [activeTabKey, setActiveTabKey] = useState<NetworkingTabKey>('networks');

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const header = TAB_HEADERS[activeTabKey];
  const HeaderIcon = header.icon;

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>Networking</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <HeaderIcon />
          </Icon>
          {header.title}
        </Content>
        <Content component="p">{header.subtitle}</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTabKey}
          onSelect={(_event, eventKey) => setActiveTabKey(eventKey as NetworkingTabKey)}
          aria-label="Networking sections"
          mountOnEnter
        >
          <Tab eventKey="gateways" title={<TabTitleText>Gateways</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <GatewaysTab />
            </div>
          </Tab>
          <Tab eventKey="firewalls" title={<TabTitleText>Firewalls</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <FirewallsTab />
            </div>
          </Tab>
          <Tab eventKey="switches" title={<TabTitleText>Switches</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SwitchesTab />
            </div>
          </Tab>
          <Tab eventKey="networks" title={<TabTitleText>Networks</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <NetworksTab />
            </div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export default Networks;
